import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Avatar, Button, TextField, Chip, Tooltip, Dialog, DialogContent, DialogActions, Divider, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import LogoLoader from "../components/Common/LogoLoader";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import LogoutIcon from "@mui/icons-material/Logout";
import { connectSocket, getSocket } from "../api/socket";
import client from "../api/client";

const API = process.env.REACT_APP_AUTH_API
  ? process.env.REACT_APP_AUTH_API.replace("/api/auth", "/api")
  : "/api";

const STATUS_CONFIG = {
  online: { color: "#10b981", label: "Online" },
  busy:   { color: "#f59e0b", label: "Busy" },
  offline:{ color: "#6b7280", label: "Offline" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};


const LiveChat = () => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [activeChats, setActiveChats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [agentStatus, setAgentStatus] = useState("online");
  const [visitorTyping, setVisitorTyping] = useState(false);
  const [unreadMap, setUnreadMap] = useState({});
  const [stats, setStats] = useState({ resolvedToday: 0, resolvedTotal: 0, activeNow: 0 });
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferAgents, setTransferAgents] = useState([]);
  const [selectedTransferAgent, setSelectedTransferAgent] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [cannedList, setCannedList] = useState([]);
  const [showCanned, setShowCanned] = useState(false);
  const [cannedFilter, setCannedFilter] = useState("");
  const [resolveConfirmOpen, setResolveConfirmOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const prevQueueLen = useRef(0);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const role = user?.user?.role || user?.role;
  const agentName = user?.user?.fullName || user?.fullName || "Agent";
  const agentInitial = agentName.charAt(0).toUpperCase();

  useEffect(() => {
    if (!user?.token) { navigate("/login"); return; }
    if (role !== "agent" && role !== "supervisor") navigate("/app/dashboard");
  }, []);

  const fetchAll = async () => {
    try {
      const [q, a, s, c] = await Promise.all([
        client.get(`${API}/live-chat/queue`),
        client.get(`${API}/live-chat/active`),
        client.get(`${API}/live-chat/stats`),
        client.get(`${API}/canned`),
      ]);
      const newLen = q.data.length;
      if (newLen > prevQueueLen.current) setUnreadMap(u => ({ ...u, _queue: (u._queue || 0) + (newLen - prevQueueLen.current) }));
      prevQueueLen.current = newLen;
      setQueue(q.data);
      setActiveChats(a.data);
      setStats(s.data || { resolvedToday: 0, resolvedTotal: 0, activeNow: 0 });
      setCannedList(c.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  const fetchMessages = async (chat) => {
    try {
      const res = await client.get(`${API}/conversation/session/${chat.chatbotId}/${chat.sessionId}`);
      setMessages(res.data.messages || []);
    } catch {}
  };


  useEffect(() => {
    fetchAll();
    const poll = setInterval(fetchAll, 10000);
    const socket = connectSocket();
    const agentId = user?.user?._id || user?._id;
    const ownerId = user?.user?.ownerId || user?.ownerId || agentId;
    if (socket) {
      socket.emit("join-agent", { agentId, ownerId });
      socket.on("live-request", fetchAll);
      socket.on("queue-update", fetchAll);
      socket.on("chat-transferred", fetchAll);
      socket.on("visitor-typing", ({ chatbotId, sessionId }) => {
        setSelected(prev => {
          if (prev?.chatbotId === chatbotId && prev?.sessionId === sessionId) {
            setVisitorTyping(true);
            setTimeout(() => setVisitorTyping(false), 3000);
          }
          return prev;
        });
      });
      socket.on("message-update", ({ chatbotId, sessionId }) => {
        setSelected(prev => {
          if (prev?.chatbotId === chatbotId && prev?.sessionId === sessionId) {
            fetchMessages(prev);
            const s = connectSocket();
            if (s) s.emit("message-seen", { chatbotId, sessionId });
          } else {
            setUnreadMap(u => ({ ...u, [sessionId]: (u[sessionId] || 0) + 1 }));
            try {
              const ctx = new (window.AudioContext || window.webkitAudioContext)();
              const o = ctx.createOscillator(); const g = ctx.createGain();
              o.connect(g); g.connect(ctx.destination);
              o.frequency.setValueAtTime(1200, ctx.currentTime);
              g.gain.setValueAtTime(0.1, ctx.currentTime);
              g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
              o.start(); o.stop(ctx.currentTime + 0.2);
            } catch {}
            document.title = "💬 New message!";
            setTimeout(() => { document.title = "Live Chat"; }, 3000);
          }
          return prev;
        });
      });
    }
    return () => {
      clearInterval(poll);
      const s = getSocket();
      if (s) {
        s.off("live-request", fetchAll); s.off("queue-update", fetchAll);
        s.off("chat-transferred", fetchAll); s.off("visitor-typing");
        s.off("message-update");
        s.emit("leave-agent", { agentId, ownerId });
      }
    };
  }, []);

  useEffect(() => {
    if (selected) {
      setMessages([]);
      fetchMessages(selected);
      const socket = connectSocket();
      if (socket) {
        socket.emit("join-conversation", { chatbotId: selected.chatbotId, sessionId: selected.sessionId });
        socket.emit("message-seen", { chatbotId: selected.chatbotId, sessionId: selected.sessionId });
      }
    } else {
      setMessages([]);
    }
    return () => {
      if (selected) {
        const s = getSocket();
        if (s) s.emit("leave-conversation", { chatbotId: selected.chatbotId, sessionId: selected.sessionId });
      }
    };
  }, [selected?._id, selected?.sessionId]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);


  const handleAccept = async (chat) => {
    try {
      await client.post(`${API}/live-chat/accept`, { chatbotId: chat.chatbotId, sessionId: chat.sessionId });
      await fetchAll();
      setSelected({ ...chat, status: "live_active" });
    } catch {}
  };

  const handleSend = async () => {
    if (!input.trim() || !selected) return;
    const text = input.trim();
    setInput("");
    setMessages(prev => [...prev, { sender: "agent", text, createdAt: new Date().toISOString() }]);
    try {
      await client.post(`${API}/live-chat/message`, { chatbotId: selected.chatbotId, sessionId: selected.sessionId, text });
    } catch {}
  };

  const handleResolve = async () => {
    if (!selected) return;
    try {
      await client.post(`${API}/live-chat/resolve`, { chatbotId: selected.chatbotId, sessionId: selected.sessionId });
      setSelected(null); setMessages([]); fetchAll();
    } catch {}
    setResolveConfirmOpen(false);
  };

  const handleTransferOpen = async () => {
    try {
      const res = await client.get(`${API}/agents`);
      const myId = user?.user?._id || user?._id;
      setTransferAgents(res.data.filter(a => String(a._id) !== String(myId)));
    } catch { setTransferAgents([]); }
    setSelectedTransferAgent("");
    setTransferOpen(true);
  };

  const handleTransfer = async () => {
    if (!selectedTransferAgent || !selected) return;
    setTransferring(true);
    try {
      await client.post(`${API}/live-chat/transfer`, { chatbotId: selected.chatbotId, sessionId: selected.sessionId, toAgentId: selectedTransferAgent });
      setTransferOpen(false); setSelected(null); setMessages([]); fetchAll();
    } catch {}
    finally { setTransferring(false); }
  };

  const cycleStatus = () => {
    const order = ["online", "busy", "offline"];
    const next = order[(order.indexOf(agentStatus) + 1) % 3];
    setAgentStatus(next);
    const backendStatus = next === "busy" ? "online" : next;
    fetch("/api/agents/status", { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: backendStatus }) }).catch(() => {});
  };

  const handleLogout = async () => {
    try { await fetch("/api/agents/status", { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: "offline" }) }); } catch {}
    navigate("/login");
  };

  if (loading) return <LogoLoader message="Loading..." />;

  const allChats = [
    ...queue.map(c => ({ ...c, _type: "queue" })),
    ...activeChats.map(c => ({ ...c, _type: "active" })),
  ];


  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden", bgcolor: "#0f172a" }}>

      {/* Sidebar */}
      <Box sx={{ width: 320, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: "1px solid #1e293b" }}>
        {/* Agent Header */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: "1px solid #1e293b" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ position: "relative" }}>
                <Avatar sx={{ width: 42, height: 42, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", fontSize: 16, fontWeight: 800, boxShadow: "0 4px 12px rgba(99,102,241,0.4)" }}>{agentInitial}</Avatar>
                <Box sx={{ position: "absolute", bottom: 1, right: 1, width: 11, height: 11, borderRadius: "50%", bgcolor: STATUS_CONFIG[agentStatus].color, border: "2px solid #0f172a" }} />
              </Box>
              <Box>
                <Typography fontSize={14} fontWeight={800} color="#f1f5f9">{agentName}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }} onClick={cycleStatus}>
                  <FiberManualRecordIcon sx={{ fontSize: 8, color: STATUS_CONFIG[agentStatus].color }} />
                  <Typography fontSize={11} color={STATUS_CONFIG[agentStatus].color} fontWeight={600}>{STATUS_CONFIG[agentStatus].label}</Typography>
                </Box>
              </Box>
            </Box>
            <Tooltip title="Logout">
              <IconButton size="small" onClick={handleLogout} sx={{ color: "#475569", "&:hover": { color: "#ef4444", bgcolor: "#1e293b" } }}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {/* Stats */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {[
              { label: "Today", value: stats.resolvedToday, color: "#10b981", bg: "#052e16" },
              { label: "Active", value: stats.activeNow, color: "#6366f1", bg: "#1e1b4b" },
              { label: "Queue", value: queue.length, color: "#f59e0b", bg: "#451a03" },
              { label: "Total", value: stats.resolvedTotal, color: "#94a3b8", bg: "#1e293b" },
            ].map(s => (
              <Box key={s.label} sx={{ flex: 1, bgcolor: s.bg, borderRadius: 1.5, p: 1, textAlign: "center" }}>
                <Typography fontSize={16} fontWeight={800} color={s.color}>{s.value}</Typography>
                <Typography fontSize={9} color="#475569" fontWeight={600}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Conversations label */}
        <Box sx={{ px: 2.5, pt: 1.5, pb: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography fontSize={11} fontWeight={700} color="#475569" sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}>Conversations</Typography>
          {queue.length > 0 && (
            <Chip label={`${queue.length} waiting`} size="small"
              icon={<NotificationsActiveIcon sx={{ fontSize: "11px !important", color: "#f59e0b !important" }} />}
              sx={{ bgcolor: "#451a03", color: "#fbbf24", fontWeight: 700, fontSize: 10, height: 18 }} />
          )}
        </Box>

        {/* Chat list */}
        <Box sx={{ flex: 1, overflowY: "auto", "&::-webkit-scrollbar": { width: 3 }, "&::-webkit-scrollbar-thumb": { bgcolor: "#1e293b" } }}>
          {allChats.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
              <SupportAgentIcon sx={{ fontSize: 44, color: "#1e293b", mb: 1.5 }} />
              <Typography fontSize={13} color="#334155" fontWeight={600}>No conversations yet</Typography>
            </Box>
          ) : allChats.map((c) => {
            const isSelected = selected?.sessionId === c.sessionId;
            const isQueue = c._type === "queue";
            const unread = unreadMap[c.sessionId] || 0;
            const lastMsg = c.messages?.[c.messages.length - 1];
            const preview = lastMsg ? ((lastMsg.sender === "agent" ? "You: " : "") + lastMsg.text).slice(0, 30) : "No messages";
            return (
              <Box key={c.sessionId}
                onClick={() => { if (!isQueue) { setSelected(c); setUnreadMap(u => ({ ...u, [c.sessionId]: 0 })); } }}
                sx={{ px: 2.5, py: 1.5, cursor: isQueue ? "default" : "pointer", bgcolor: isSelected ? "#1e293b" : "transparent", borderLeft: isSelected ? "3px solid #6366f1" : "3px solid transparent", "&:hover": { bgcolor: isQueue ? "transparent" : "#1e293b" }, transition: "all 0.15s" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ position: "relative", flexShrink: 0 }}>
                    <Avatar sx={{ width: 38, height: 38, background: isQueue ? "linear-gradient(135deg, #92400e, #b45309)" : "linear-gradient(135deg, #1e3a5f, #1e40af)", fontSize: 14, fontWeight: 700 }}>
                      {(c.userName || "V").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ position: "absolute", bottom: 0, right: 0, width: 9, height: 9, borderRadius: "50%", bgcolor: isQueue ? "#f59e0b" : "#10b981", border: "2px solid #0f172a" }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography fontSize={13} fontWeight={unread > 0 ? 800 : 600} color="#f1f5f9" noWrap>{c.userName || "Visitor"}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {unread > 0 && <Box sx={{ minWidth: 18, height: 18, borderRadius: "50%", bgcolor: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", px: 0.5 }}><Typography fontSize={10} fontWeight={800} color="#fff">{unread}</Typography></Box>}
                        <Typography fontSize={10} color="#334155">{timeAgo(c.updatedAt)}</Typography>
                      </Box>
                    </Box>
                    <Typography fontSize={11} color={unread > 0 ? "#64748b" : "#334155"} noWrap fontWeight={unread > 0 ? 600 : 400}>{preview}</Typography>
                  </Box>
                </Box>
                {isQueue && (
                  <Button fullWidth size="small" variant="contained"
                    onClick={(e) => { e.stopPropagation(); handleAccept(c); }}
                    sx={{ mt: 1, textTransform: "none", fontSize: 11, borderRadius: 1.5, py: 0.5, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", boxShadow: "0 2px 8px rgba(99,102,241,0.3)" }}>
                    Accept Chat
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>


      {/* Chat Window */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: "#0f172a" }}>
        {!selected ? (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ width: 80, height: 80, borderRadius: "24px", background: "linear-gradient(135deg, #1e293b, #334155)", display: "flex", alignItems: "center", justifyContent: "center", mb: 2.5 }}>
              <SupportAgentIcon sx={{ fontSize: 40, color: "#475569" }} />
            </Box>
            <Typography fontWeight={800} fontSize={18} color="#334155">No conversation selected</Typography>
            <Typography fontSize={13} color="#1e293b" mt={0.5}>Accept a chat from the queue to begin</Typography>
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #1e293b", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ width: 40, height: 40, background: "linear-gradient(135deg, #1e3a5f, #1e40af)", fontWeight: 700 }}>
                  {(selected.userName || "V").charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography fontWeight={700} fontSize={15} color="#f1f5f9">{selected.userName || "Visitor"}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981" }} />
                    <Typography fontSize={11} color="#10b981" fontWeight={600}>Live · {selected.botName}</Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" size="small" startIcon={<SwapHorizIcon fontSize="small" />} onClick={handleTransferOpen}
                  sx={{ textTransform: "none", borderRadius: 2, fontSize: 12, fontWeight: 700, borderColor: "#334155", color: "#94a3b8", "&:hover": { borderColor: "#6366f1", color: "#6366f1", bgcolor: "transparent" } }}>
                  Transfer
                </Button>
                <Button variant="contained" size="small" startIcon={<CheckCircleOutlineIcon fontSize="small" />} onClick={() => setResolveConfirmOpen(true)}
                  sx={{ textTransform: "none", borderRadius: 2, fontSize: 12, fontWeight: 700, background: "linear-gradient(135deg, #059669, #10b981)", boxShadow: "0 2px 8px rgba(16,185,129,0.3)" }}>
                  Resolve
                </Button>
              </Box>
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 1.5, "&::-webkit-scrollbar": { width: 4 }, "&::-webkit-scrollbar-thumb": { bgcolor: "#1e293b", borderRadius: 2 } }}>
              {messages.map((m, i) => {
                const isAgent = m.sender === "agent" || m.sender === "admin";
                const isBot = m.sender === "bot";
                const prevM = messages[i - 1];
                const isFirst = !prevM || prevM.sender !== m.sender;
                return (
                  <Box key={i} sx={{ display: "flex", justifyContent: isAgent ? "flex-end" : "flex-start", gap: 1, alignItems: "flex-end",
                    animation: "fadeUp 0.2s ease", "@keyframes fadeUp": { from: { opacity: 0, transform: "translateY(8px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
                    {!isAgent && isFirst && (
                      <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, bgcolor: isBot ? "#1e293b" : "#1e3a5f", color: isBot ? "#475569" : "#60a5fa", flexShrink: 0 }}>
                        {isBot ? "B" : "U"}
                      </Avatar>
                    )}
                    {!isAgent && !isFirst && <Box sx={{ width: 28, flexShrink: 0 }} />}
                    <Box sx={{ maxWidth: "65%" }}>
                      <Box sx={{ px: 2, py: 1.2, borderRadius: isAgent ? "16px 16px 4px 16px" : "16px 16px 16px 4px", background: isAgent ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : isBot ? "#1e293b" : "#1e3a5f", color: "#f1f5f9", fontSize: 13.5, lineHeight: 1.6, boxShadow: isAgent ? "0 3px 12px rgba(99,102,241,0.3)" : "0 1px 4px rgba(0,0,0,0.3)" }}>
                        {m.text}
                      </Box>
                      <Typography fontSize={10} color="#334155" sx={{ mt: 0.3, textAlign: isAgent ? "right" : "left" }}>{timeAgo(m.createdAt)}</Typography>
                    </Box>
                    {isAgent && (
                      <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", flexShrink: 0 }}>{agentInitial}</Avatar>
                    )}
                  </Box>
                );
              })}
              {visitorTyping && (
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: "#1e3a5f", color: "#60a5fa" }}>U</Avatar>
                  <Box sx={{ px: 2, py: 1.2, borderRadius: "16px 16px 16px 4px", bgcolor: "#1e293b", display: "flex", gap: 0.5 }}>
                    {[0,1,2].map(i => <Box key={i} sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#475569", animation: "bounce 1.4s infinite", animationDelay: `${i*0.2}s`, "@keyframes bounce": { "0%,80%,100%": { transform: "scale(0.6)" }, "40%": { transform: "scale(1)" } } }} />)}
                  </Box>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Canned popup */}
            {showCanned && (
              <Box sx={{ mx: 3, mb: 1, bgcolor: "#1e293b", borderRadius: 2, overflow: "hidden", border: "1px solid #334155", maxHeight: 200, overflowY: "auto" }}>
                <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #334155" }}>
                  <TextField size="small" fullWidth placeholder="Search..." value={cannedFilter} onChange={e => setCannedFilter(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 12, color: "#f1f5f9", "& fieldset": { borderColor: "#334155" } } }} />
                </Box>
                {cannedList.filter(c => !cannedFilter || c.shortcut.includes(cannedFilter) || c.text.toLowerCase().includes(cannedFilter.toLowerCase())).map(c => (
                  <Box key={c._id} onClick={() => { setInput(c.text); setShowCanned(false); setCannedFilter(""); }}
                    sx={{ px: 2, py: 1, cursor: "pointer", "&:hover": { bgcolor: "#334155" }, display: "flex", gap: 1.5, alignItems: "center" }}>
                    <Chip label={`/${c.shortcut}`} size="small" sx={{ bgcolor: "#312e81", color: "#a5b4fc", fontWeight: 700, fontSize: 10 }} />
                    <Typography fontSize={12} color="#94a3b8" noWrap>{c.text}</Typography>
                  </Box>
                ))}
                {cannedList.length === 0 && <Typography fontSize={12} color="#475569" sx={{ p: 2, textAlign: "center" }}>No canned responses yet.</Typography>}
              </Box>
            )}

            {/* Input */}
            <Box sx={{ px: 3, py: 2, borderTop: "1px solid #1e293b", display: "flex", gap: 1.5, alignItems: "flex-end" }}>
              <Tooltip title="Quick replies">
                <Box onClick={() => setShowCanned(s => !s)} sx={{ cursor: "pointer", color: showCanned ? "#6366f1" : "#334155", "&:hover": { color: "#6366f1" }, mb: 0.5, transition: "color 0.2s" }}>
                  <FlashOnIcon fontSize="small" />
                </Box>
              </Tooltip>
              <TextField fullWidth multiline maxRows={4} size="small" placeholder="Type your reply... (Enter to send)" value={input}
                onChange={(e) => { setInput(e.target.value); if (selected) { const s = getSocket(); if (s) s.emit("agent-typing", { chatbotId: selected.chatbotId, sessionId: selected.sessionId }); } }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: "#1e293b", fontSize: 13.5, color: "#f1f5f9", "& fieldset": { borderColor: "#334155" }, "&:hover fieldset": { borderColor: "#6366f1" }, "&.Mui-focused fieldset": { borderColor: "#6366f1" } } }} />
              <Button variant="contained" onClick={handleSend} disabled={!input.trim()}
                sx={{ borderRadius: 2.5, minWidth: 46, height: 40, px: 1.5, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", flexShrink: 0 }}>
                <SendIcon fontSize="small" />
              </Button>
            </Box>
          </>
        )}
      </Box>


      {/* Right Panel */}
      {selected && (
        <Box sx={{ width: 240, flexShrink: 0, bgcolor: "#0f172a", borderLeft: "1px solid #1e293b", display: "flex", flexDirection: "column", overflowY: "auto" }}>
          <Box sx={{ px: 2.5, py: 2.5 }}>
            <Typography fontSize={11} fontWeight={700} color="#475569" sx={{ textTransform: "uppercase", letterSpacing: 0.8, mb: 2 }}>Visitor Info</Typography>
            <Box sx={{ textAlign: "center", mb: 2.5 }}>
              <Avatar sx={{ width: 52, height: 52, background: "linear-gradient(135deg, #1e3a5f, #1e40af)", fontWeight: 800, fontSize: 22, mx: "auto", mb: 1 }}>
                {(selected.userName || "V").charAt(0).toUpperCase()}
              </Avatar>
              <Typography fontWeight={700} fontSize={14} color="#f1f5f9">{selected.userName || "Visitor"}</Typography>
              <Chip label="Live" size="small" sx={{ bgcolor: "#052e16", color: "#10b981", fontWeight: 700, fontSize: 10, mt: 0.5 }} />
            </Box>
            {[
              { label: "Bot", value: selected.botName || "—" },
              { label: "Messages", value: messages.length },
              { label: "Session", value: `...${(selected.sessionId || "").slice(-6)}` },
              { label: "Started", value: timeAgo(selected.createdAt) },
            ].map(item => (
              <Box key={item.label} sx={{ display: "flex", justifyContent: "space-between", py: 0.8, borderBottom: "1px solid #1e293b" }}>
                <Typography fontSize={11} color="#475569">{item.label}</Typography>
                <Typography fontSize={11} fontWeight={600} color="#94a3b8">{item.value}</Typography>
              </Box>
            ))}
          </Box>
          <Divider sx={{ borderColor: "#1e293b" }} />
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography fontSize={11} fontWeight={700} color="#475569" sx={{ textTransform: "uppercase", letterSpacing: 0.8, mb: 1.5 }}>Quick Replies</Typography>
            {cannedList.slice(0, 6).map(c => (
              <Box key={c._id} onClick={() => setInput(c.text)}
                sx={{ px: 1.5, py: 0.8, borderRadius: 1.5, cursor: "pointer", "&:hover": { bgcolor: "#1e293b" }, mb: 0.5, border: "1px solid #1e293b", transition: "all 0.15s" }}>
                <Typography fontSize={10} fontWeight={700} color="#6366f1">/{c.shortcut}</Typography>
                <Typography fontSize={11} color="#64748b" noWrap>{c.text}</Typography>
              </Box>
            ))}
            {cannedList.length === 0 && <Typography fontSize={11} color="#334155">No quick replies yet.</Typography>}
          </Box>
        </Box>
      )}

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: "#0f172a", border: "1px solid #1e293b", borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SwapHorizIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize={16} color="#fff">Transfer Chat</Typography>
            <Typography fontSize={12} color="rgba(255,255,255,0.7)">Assign to another agent</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {transferAgents.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: "16px", bgcolor: "#1e293b", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
                <Typography fontSize={28}>👥</Typography>
              </Box>
              <Typography fontWeight={700} fontSize={14} color="#f1f5f9" mb={0.5}>No other agents</Typography>
              <Typography fontSize={12} color="#475569">You are the only agent available.</Typography>
            </Box>
          ) : (() => {
            const onlineAgents = transferAgents.filter(a => a.agentStatus === "online");
            const offlineAgents = transferAgents.filter(a => a.agentStatus !== "online");
            const selObj = transferAgents.find(a => a._id === selectedTransferAgent);
            return (
              <>
                {onlineAgents.length === 0 && (
                  <Box sx={{ p: 1.5, bgcolor: "#451a03", border: "1px solid #92400e", borderRadius: 2, mb: 2, display: "flex", gap: 1 }}>
                    <Typography fontSize={14}>⚠️</Typography>
                    <Box>
                      <Typography fontSize={12} fontWeight={700} color="#fbbf24">All agents are offline</Typography>
                      <Typography fontSize={11} color="#d97706">Chat will be assigned when they come online.</Typography>
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {onlineAgents.length > 0 && <Typography fontSize={10} fontWeight={700} color="#475569" sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}>Online</Typography>}
                  {onlineAgents.map(a => (
                    <Box key={a._id} onClick={() => setSelectedTransferAgent(a._id)}
                      sx={{ p: 1.5, borderRadius: 2, cursor: "pointer", border: "2px solid", borderColor: selectedTransferAgent === a._id ? "#6366f1" : "#1e293b", bgcolor: selectedTransferAgent === a._id ? "#1e1b4b" : "#1e293b", display: "flex", alignItems: "center", gap: 1.5, transition: "all 0.15s", "&:hover": { borderColor: "#6366f1" } }}>
                      <Avatar sx={{ width: 36, height: 36, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", fontWeight: 700, fontSize: 14 }}>{a.fullName.charAt(0).toUpperCase()}</Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontSize={13} fontWeight={700} color="#f1f5f9">{a.fullName}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981" }} />
                          <Typography fontSize={11} color="#10b981" fontWeight={600}>{a.role} · Online</Typography>
                        </Box>
                      </Box>
                      {selectedTransferAgent === a._id && <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center" }}><Typography fontSize={12} color="#fff">✓</Typography></Box>}
                    </Box>
                  ))}
                  {offlineAgents.length > 0 && <Typography fontSize={10} fontWeight={700} color="#475569" sx={{ textTransform: "uppercase", letterSpacing: 0.8, mt: 0.5 }}>Offline</Typography>}
                  {offlineAgents.map(a => (
                    <Box key={a._id} onClick={() => setSelectedTransferAgent(a._id)}
                      sx={{ p: 1.5, borderRadius: 2, cursor: "pointer", border: "2px solid", borderColor: selectedTransferAgent === a._id ? "#475569" : "#1e293b", bgcolor: "#1e293b", display: "flex", alignItems: "center", gap: 1.5, opacity: 0.6, transition: "all 0.15s", "&:hover": { opacity: 1, borderColor: "#475569" } }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: "#334155", color: "#64748b", fontWeight: 700, fontSize: 14 }}>{a.fullName.charAt(0).toUpperCase()}</Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontSize={13} fontWeight={700} color="#94a3b8">{a.fullName}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#475569" }} />
                          <Typography fontSize={11} color="#475569" fontWeight={600}>{a.role} · Offline</Typography>
                        </Box>
                      </Box>
                      {selectedTransferAgent === a._id && <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#475569", display: "flex", alignItems: "center", justifyContent: "center" }}><Typography fontSize={12} color="#fff">✓</Typography></Box>}
                    </Box>
                  ))}
                </Box>
                {selObj && selObj.agentStatus !== "online" && (
                  <Box sx={{ mt: 2, p: 1.2, bgcolor: "#451a03", border: "1px solid #92400e", borderRadius: 1.5, display: "flex", gap: 0.8 }}>
                    <Typography fontSize={13}>⚠️</Typography>
                    <Typography fontSize={12} color="#fbbf24">{selObj.fullName} is offline. They'll receive this chat when they come online.</Typography>
                  </Box>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button onClick={() => setTransferOpen(false)} fullWidth variant="outlined"
            sx={{ textTransform: "none", borderRadius: 2, borderColor: "#334155", color: "#64748b", "&:hover": { borderColor: "#475569" } }}>
            Cancel
          </Button>
          {transferAgents.length > 0 && (
            <Button variant="contained" onClick={handleTransfer} disabled={!selectedTransferAgent || transferring} fullWidth
              sx={{ textTransform: "none", borderRadius: 2, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", "&.Mui-disabled": { background: "linear-gradient(135deg, #3730a3, #4c1d95)", color: "#6366f1", opacity: 1 } }}>
              {transferring ? "Transferring..." : "Transfer Chat"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Resolve Confirmation Dialog */}
      <Dialog open={resolveConfirmOpen} onClose={() => setResolveConfirmOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: "#0f172a", border: "1px solid #1e293b", borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ background: "linear-gradient(135deg, #059669, #10b981)", px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CheckCircleOutlineIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize={16} color="#fff">Resolve Chat</Typography>
            <Typography fontSize={12} color="rgba(255,255,255,0.7)">End this conversation</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Typography fontSize={14} color="#e2e8f0" mb={1}>Are you sure you want to resolve this chat?</Typography>
          <Typography fontSize={12} color="#64748b">The visitor will be notified that the chat has ended. This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button onClick={() => setResolveConfirmOpen(false)} fullWidth variant="outlined"
            sx={{ textTransform: "none", borderRadius: 2, borderColor: "#334155", color: "#64748b", "&:hover": { borderColor: "#475569" } }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleResolve} fullWidth
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, background: "linear-gradient(135deg, #059669, #10b981)", "&:hover": { background: "linear-gradient(135deg, #047857, #059669)" } }}>
            Yes, Resolve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiveChat;
